/**
 * IB-285/IB-211 S2 (FR-M7.4) — client-side cache of "this Program was cloned from that Program",
 * captured at the moment of a clone so the Studio workspace can show a provenance banner instantly
 * (before the fresh `GET /programs/{id}` round-trip completes) and with a richer label (source
 * jurisdiction/level/code) than the bare id alone.
 *
 * **No longer the primary source (closed 2026-08-23).** `ProgramView.clonedFromProgramId` now
 * projects the V39 `program.cloned_from_program_id` column through `GET /programs/{id}` — that is
 * the SERVER fact, reachable from any device/browser/session/author, and `ProgramWorkspaceScreen`
 * reads it first. This cache remains only as an **immediate-paint fallback** for the instant right
 * after a clone (same device/browser, before/while the first fetch resolves) and as a source of the
 * richer display fields (jurisdiction/level/code/asOf/structureVersion) the bare id-only server field
 * doesn't carry by itself — `ProgramWorkspaceScreen` falls back to a one-shot `getProgram(sourceId)`
 * lookup for those when this cache has nothing (e.g. a different browser/session).
 */

const KEY_PREFIX = 'iw.cartridgeStudio.cloneProvenance.'

export interface CloneProvenance {
  sourceProgramId: string
  sourceJurisdiction: string
  sourceLevel: string
  sourceCode: string
  sourceStructureVersion: number
  asOf: string
  clonedAt: string
}

export function saveCloneProvenance(newProgramId: string, provenance: CloneProvenance): void {
  try {
    window.localStorage.setItem(KEY_PREFIX + newProgramId, JSON.stringify(provenance))
  } catch {
    // Storage unavailable (private mode, quota) — the clone itself already succeeded server-side
    // and is fully provable via the cartridge.cloned.v1 audit Event; never block on this cache.
  }
}

export function getCloneProvenance(programId: string): CloneProvenance | null {
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + programId)
    return raw ? (JSON.parse(raw) as CloneProvenance) : null
  } catch {
    return null
  }
}
